import {
  AI_CONSENT,
  type ChatQuestion,
  INTAKE_CHAT_V2,
  INTAKE_SUMMARY,
  RECIPIENT,
} from '@/karehero-app/tell-us-once/intake/content';
import { DONE } from '@/karehero-app/tell-us-once/intake/ProcessingChat.content';

/**
 * Onboarding 1.1: the same flow, warmed up.
 *
 * From "Warming up onboarding" (6 October), the doc answering Steph's feedback
 * that the flow asks without giving anything back, the words are correct but
 * cold, and nothing in it feels like KareHero. Its recommended set, minus what
 * waits on someone else:
 *
 * - The copy table (section 5), and 2D: the open ask, warmer.
 * - 1A: say what the answers are for, after the names.
 * - 1B: a number that matters, straight after the fork.
 * - 1C: the care experts behind it, after the names.
 * - 2A: consent, plain and honest.
 * - 3A: the wait shows the work, resolving into 3C.
 * - 4A: Keni named once.
 *
 * Not here: 4B (a still Keni at three pauses) waits on the brand audit's final
 * drawing, and 2C waits on legal.
 *
 * Same questions, same ids, same order. Everything is copy, or a card attached
 * to a step that already exists: saved progress is by step index, and a new
 * step would reset anyone mid-flow.
 */

const CARER_FIRST_NAME = 'Vez';

/** Per question id, what 1.1 changes. Anything not listed is 1.0's. */
const CHANGES: Record<string, Partial<ChatQuestion>> = {
  fork: {
    // 4A: Keni named once, in the first bubble. The orb stays the only visual.
    says: [
      "Hi, I'm Keni. I'll ask you a few questions, and our care experts will take it from there.",
    ],
  },
  'carer-name': {
    // 1B, keyed to the fork's carer answer: the first thing given back.
    card: {
      label: 'Worth knowing',
      body: "Most families we help are owed money they aren't claiming. Attendance Allowance alone is £76.70 to £114.60 a week, and over a million eligible households miss out on it.",
      note: "We'll check what applies to you as we go.",
    },
    ask: 'First, is Vez Maxwell the name on your official letters? If not, type it as it appears there.',
  },
  'carer-preferred-name': {
    ask: 'And what should we call you?',
  },
  who: {
    // 1A then 1C: what the answers are for, and who reads them.
    says: [
      `Thanks, ${CARER_FIRST_NAME}. Here's how this works: everything you tell us from now on goes into one care profile for the person you look after. We use it to fill in forms like Attendance Allowance, so you only have to say things once.`,
    ],
    card: {
      label: 'Who reads this',
      body: 'Yasmin, Brett and Abby, our care experts, read everything you share here. One of them will call you to go through it.',
      note: 'Nothing is decided by a computer on its own.',
      people: ['Yasmin', 'Brett', 'Abby'],
    },
    ask: 'Who are you looking after? Their full name, as it appears on their letters.',
  },
  situation: {
    // 2D.
    ask: `Now tell us about ${RECIPIENT} and what looking after her involves. Talk or type, whichever is easier. There's no right way to do this, and you can stop and start as often as you like.`,
    hint: 'Tap the orb to record and tap it again to stop. Take your time. If you have a letter or a hospital summary to hand, add it with the +.',
  },
  'anything-more': {
    ask: "Anything else you'd like us to know? Add as much as you like.",
    asides: [{ icon: 'chevron-right', label: "That's everything", tone: 'go' }],
  },
};

export const INTAKE_CHAT_V1_1: ChatQuestion[] = INTAKE_CHAT_V2.map((q) => ({
  ...q,
  ...CHANGES[q.id],
}));

/** You're in. Says what they get, rather than "shape the right support". */
export const YOURE_IN_V1_1 =
  "Over the next few minutes we'll ask about you and the person you look after, and start putting their care profile together. Say as much or as little as you like.";

/** 2A: same screen, same two buttons, plain about what AI is for. */
export const AI_CONSENT_V1_1: typeof AI_CONSENT = {
  title: 'Before we start',
  body: 'You can tell us about your situation in your own words: typed, spoken, or as a photo of a letter. We use AI to help our care experts read and organise what you share, so nothing gets lost and you never have to say it twice. A person is always involved.',
  policyLead: 'Read',
  policyLink: 'how we use AI',
  accept: "That's fine with me",
  decline: "I'd rather not",
};

/** The reading, said with their name and a time. */
export const PROCESSING_OPENING_V1_1 = `Thanks, ${CARER_FIRST_NAME}. We're going through everything you've told us now. It takes a couple of minutes, and there's no rush on your side.`;

/** The notice when it is done. Half the length, same meaning. */
export const PROCESSING_DONE_V1_1: typeof DONE = {
  ...DONE,
  title: "We've read everything you told us",
  body: "Have a look when you're ready. There's no rush.",
};

/**
 * 3A, then 3C: what the notice area holds while the reading runs.
 *
 * The steps are keyed to what they sent. In the app each ticks as its event
 * lands (per-document extraction, then the summary); here they tick on a
 * timer. "Checking what she could claim" is timed until the eligibility work
 * exists, so it says checking, never found.
 */
export const PROCESSING_WORK_V1_1 = {
  label: 'Reading what you sent',
  steps: [
    'Listened to your voice note (1:12)',
    'Read the discharge summary',
    `Checking what ${RECIPIENT} could claim`,
    'Writing up her care profile',
  ],
  then: {
    label: "What we're putting together",
    items: [
      `${RECIPIENT}'s care profile, filled in from what you said`,
      'What she could claim',
      'A call with a care expert to go through it',
    ],
    note: "We'll ping you when it's ready. It usually takes a few minutes.",
  },
};

/** The summary when the reading went wrong. Never "wrong with your answers". */
export const INTAKE_SUMMARY_V1_1: typeof INTAKE_SUMMARY = {
  ...INTAKE_SUMMARY,
  problem:
    "We couldn't make out everything you sent. Here's what we did get. You can add to it, or a care expert can go through it with you.",
};
